import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, MapPin, ChevronDown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NAV_ITEMS, VG_ORANGE } from "../lib/brand";
import { CONTACT_INFO } from "../data/products";

// Opens the catalog PDF in a new tab for viewing (download stays a secondary action).
export const openCatalog = (url) => (e) => {
  if (e) e.preventDefault();
  window.open(url, "_blank", "noopener,noreferrer");
};

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="site-logo" className="flex items-center gap-2 group p-4">
    <img
      src="/vg-logo.webp"
      alt="van Gelder Incorporated"
      className="h-[108px] w-auto"
    />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const location = useLocation();

  const linkClass = ({ isActive } = {}) =>
    `px-1 py-2 text-[13px] font-medium transition-colors border-b-2 ${
      isActive
        ? "border-[#ED8B00] text-neutral-900"
        : "border-transparent text-neutral-600 hover:text-[#ED8B00]"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
      {/* Top info strip */}
      <div className="hidden md:block bg-white text-black text-xs">
        <div className="mx-auto max-w-7xl px-4 py-1.5 flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" style={{ color: VG_ORANGE }} />
            {CONTACT_INFO.address}
          </span>
          <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-2 hover:text-[#ED8B00]" data-testid="header-phone">
            <Phone className="h-3.5 w-3.5" style={{ color: VG_ORANGE }} />
            {CONTACT_INFO.phone}
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4">
        <div className="relative flex h-40 items-center justify-between">
          <Logo />

          {/* Desktop nav (centered) */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 absolute left-1/2 -translate-x-1/2" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) =>
              item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openCatalog(item.href)}
                  data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className={linkClass()}
                >
                  {item.label}
                </a>
              ) : item.children && item.children.length ? (
                <div key={item.to} className="relative group" data-testid={`nav-dropdown-${item.label.replace(/\s+/g, "-").toLowerCase()}`}>
                  <NavLink to={item.to} end={item.to === "/"} className={`${linkClass()} flex items-center gap-1`}>
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </NavLink>
                  <div className="invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 absolute left-1/2 -translate-x-1/2 top-full pt-2 z-50">
                    <div className="w-60 max-h-[70vh] overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg py-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="block px-4 py-2 text-sm text-neutral-700 hover:bg-orange-50 hover:text-[#ED8B00]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  className="flex items-center justify-center h-10 w-10 rounded-md border border-neutral-200"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0 overflow-y-auto">
                <div className="p-4 border-b">
                  <Logo />
                </div>
                <nav className="flex flex-col p-2" data-testid="mobile-nav">
                  {NAV_ITEMS.map((item) => {
                    const hasChildren = item.children && item.children.length > 0;
                    const isOpenSec = openSection === item.label;
                    const slug = item.label.replace(/\s+/g, "-").toLowerCase();
                    return (
                      <div key={item.label} className="border-b border-neutral-100 last:border-0">
                        {hasChildren ? (
                          <>
                            <div className="flex items-center">
                              <NavLink
                                to={item.to}
                                end={item.to === "/"}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                  `flex-1 rounded-md px-3 py-2.5 text-sm font-medium ${
                                    isActive ? "text-[#ED8B00]" : "text-neutral-700"
                                  }`
                                }
                              >
                                {item.label}
                              </NavLink>
                              <button
                                type="button"
                                data-testid={`mobile-nav-toggle-${slug}`}
                                aria-label={`Toggle ${item.label} menu`}
                                aria-expanded={isOpenSec}
                                onClick={() => setOpenSection(isOpenSec ? null : item.label)}
                                className="flex h-9 w-9 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
                              >
                                <ChevronDown className={`h-4 w-4 transition-transform ${isOpenSec ? "rotate-180" : ""}`} />
                              </button>
                            </div>
                            {isOpenSec && (
                              <div className="ml-3 border-l border-neutral-200 pl-2 mb-1" data-testid={`mobile-nav-submenu-${slug}`}>
                                {item.children.map((child) => (
                                  <Link
                                    key={child.to}
                                    to={child.to}
                                    onClick={() => setOpen(false)}
                                    className="block rounded-md px-3 py-2 text-[13px] text-neutral-500 hover:text-[#ED8B00]"
                                  >
                                    {child.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </>
                        ) : item.href ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`mobile-nav-${slug}`}
                            onClick={(e) => {
                              openCatalog(item.href)(e);
                              setOpen(false);
                            }}
                            className="rounded-md px-3 py-2.5 text-sm font-medium block text-neutral-700 hover:bg-neutral-100"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <NavLink
                            to={item.to}
                            end={item.to === "/"}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                              `rounded-md px-3 py-2.5 text-sm font-medium block ${
                                isActive ? "bg-orange-50 text-[#ED8B00]" : "text-neutral-700 hover:bg-neutral-100"
                              }`
                            }
                          >
                            {item.label}
                          </NavLink>
                        )}
                      </div>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#A6A8A5] text-neutral-800 mt-auto" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-4 py-12 grid gap-8 md:grid-cols-3">
      <div>
        <Logo light />
        <p className="mt-4 text-sm text-neutral-700 max-w-xs">
          Your commercial flooring outfitters. Solutions for every environment.
        </p>
      </div>
      <div>
        <h4 className="font-heading text-neutral-900 font-semibold mb-3">Quick Links</h4>
        <ul className="space-y-2 text-sm">
          {NAV_ITEMS.map((i) => (
            <li key={i.label}>
              {i.href ? (
                <a
                  href={i.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openCatalog(i.href)}
                  className="hover:text-[#ED8B00]"
                >
                  {i.label}
                </a>
              ) : (
                <Link to={i.to} className="hover:text-[#ED8B00]">{i.label}</Link>
              )}
            </li>
          ))}
          <li>
            <Link to="/admin" className="hover:text-[#ED8B00]" data-testid="footer-staff-login">Staff Login</Link>
          </li>
        </ul>
      </div>
      <div>
        <h4 className="font-heading text-neutral-900 font-semibold mb-3">Contact</h4>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-2.5">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full shadow-sm ring-1 ring-white" style={{ backgroundColor: VG_ORANGE }}>
              <MapPin className="h-3.5 w-3.5 text-white" />
            </span>
            <span>{CONTACT_INFO.company}<br />{CONTACT_INFO.address}</span>
          </li>
          <li>
            <a href={CONTACT_INFO.phoneHref} className="flex items-center gap-2.5 hover:text-[#ED8B00]" data-testid="footer-phone">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full shadow-sm ring-1 ring-white" style={{ backgroundColor: VG_ORANGE }}>
                <Phone className="h-3.5 w-3.5 text-white" />
              </span>
              {CONTACT_INFO.phone}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-black/15">
      <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-neutral-700">
        <span>© {new Date().getFullYear()} van Gelder, Inc. All rights reserved.</span>
        <Link to="/privacy" className="hover:text-neutral-900">Privacy Policy</Link>
      </div>
    </div>
  </footer>
);

export const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
